/**
 * Calendar dates (a testing period, a due date) are days, not instants.
 *
 * The backend stores them as a DATE and sends them as "2025-06-01" or "2025-06-01T00:00:00Z" (midnight UTC).
 * `new Date(...)` reads that as an instant and prints it in the viewer's time zone, so anyone west of UTC sees
 * the evening before: a period starting 1 June reads "May 31" in the US. These helpers read the day as written
 * and format it in UTC. Real timestamps (anything not at exactly midnight UTC) are left in local time.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00:00(?:\.0+)?(?:Z|[+-]00:?00)?)?$/;

/** { year, month (1-12), day } when `value` is a calendar date, else null. */
export function calendarDateParts(value) {
  const m = DATE_ONLY.exec(String(value ?? '').trim());
  return m ? { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) } : null;
}

const DEFAULT_OPTS = { day: '2-digit', month: 'short', year: 'numeric' };

/** The date as the viewer's locale prints it, on the day it was written. null when `value` is not a date. */
export function formatCalendarDate(value, locale = 'en-US', options = DEFAULT_OPTS) {
  if (value == null || value === '') return null;
  const p = calendarDateParts(value);
  if (p) return new Date(Date.UTC(p.year, p.month - 1, p.day)).toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(locale, options);
}

/** A calendar date as local midnight of that day, for comparing with "today" (a Date built from the day as
 *  written, not shifted by the viewer's time zone). Anything that is not a calendar date is read as an instant. */
export function calendarDateToLocal(value) {
  if (value == null || value === '') return null;
  const p = calendarDateParts(value);
  const d = p ? new Date(p.year, p.month - 1, p.day) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "YYYY-MM-DD" of a local Date (not toISOString, which is the UTC date and is a day off east of UTC). */
export function localDateKey(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** "YYYY-MM-DD" of a calendar date value, on the day it was written. */
export function calendarDateKey(value) {
  const p = calendarDateParts(value);
  if (p) return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
  const d = calendarDateToLocal(value);
  return d ? localDateKey(d) : '';
}
